import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Home } from "lucide-react";
import { SeedMark } from "../components/SeedMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (!detail) return "Não foi possível entrar. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("seed_admin_token", data.token);
      navigate(data.role === "administrativo" ? "/admin/agenda-equipe" : "/admin");
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center p-6" data-testid="admin-login-page">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-lg bg-white p-10 shadow-sm"
        data-testid="admin-login-form"
      >
        <div className="mb-8 flex flex-col items-center gap-4">
          <SeedMark size={56} />
          <div className="text-center">
            <h1 className="font-heading text-2xl font-black tracking-tight text-seed-deep">Painel Seed Imóveis</h1>
            <p className="mt-1 text-sm text-seed-deep/50">Acesso restrito ao administrador</p>
          </div>
        </div>
        <div className="space-y-4">
          <input
            type="email"
            required
            placeholder="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-login-email"
            className="h-14 w-full rounded-md bg-seed-off px-5 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
          />
          <input
            type="password"
            required
            placeholder="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password"
            className="h-14 w-full rounded-md bg-seed-off px-5 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
          />
          {error && (
            <p className="rounded-md bg-red-50 px-4 py-3 text-sm font-semibold text-red-600" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-seed-deep py-4 font-heading text-sm font-bold text-seed-off transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              <Lock size={16} />
              {loading ? "Entrando..." : "Entrar"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/")}
              aria-label="Voltar ao site"
              title="Voltar ao site"
              data-testid="admin-login-back-site"
              className="flex items-center justify-center rounded-full border border-seed-deep/15 px-5 py-4 text-seed-deep/50 transition-colors duration-300 hover:border-seed-salad hover:text-seed-salad"
            >
              <Home size={17} />
            </button>
          </div>
        </div>
      </motion.form>
    </main>
  );
}
